/*
 * *Forward…* — one message into another conversation (X-A4 N11).
 *
 * The decision that makes this cheap: forwarding stores nothing and permits nothing. There is no
 * route, no wire change and no new action; the dialog writes the quotation into the **target
 * conversation's draft** — the same never-lose-work layer the composer reads — and goes there with
 * the caret in the box. What leaves the machine is an ordinary message that the sender read first,
 * which is the behaviour you want when the source was a private channel: the whole text is on screen,
 * in their own composer, before anything is sent.
 *
 * A draft that is already in that box is kept; the quotation is appended under it
 * (`DraftStore.append`). Anything else would be this feature deleting somebody's sentence.
 *
 * The picker is the quick switcher's scorer over the conversations this person may write in — an
 * active membership, nothing else: a public channel they have not joined has no composer to write
 * into, and a person they have never written to has no conversation yet. Both are one *New message*
 * away, and a row that cannot receive a forward would be a row that does nothing.
 */

import { useMemo, useState } from "react"
import { keyOf, useDraftStore } from "../../../core/drafts/index.ts"
import { formatDate } from "../../../core/i18n/format.ts"
import { t as coreT } from "../../../core/i18n/index.ts"
import { focusComposerWhenItArrives, scoreTarget } from "../../../core/shell/index.ts"
import { describeRow, type SwitchTarget } from "../../../core/shell/QuickSwitcher.tsx"
import type { ConversationId, ConversationView, Model } from "../../../core/store/model.ts"
import { client } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { conversationHref, messageHref } from "../../../routes/links.ts"
import { Button, Dialog, Field, FormAlert } from "../../../ui/index.ts"
import { goTo } from "../conversation/opening.ts"
import { conversationAddress, conversationTitle } from "../conversation/title.ts"
import { t } from "../i18n/t.ts"
import { chatSlice, isDirect, sidebarOrder } from "../state/selectors.ts"
import type { MessageView } from "../wire/views.ts"
import { buildForwardQuote } from "./copy.ts"
import "./forward.css"

/** How many destinations the list offers at once; the box narrows them. */
const MAX_ROWS = 20

/** One place a message can be forwarded to. */
interface Destination {
	conv: ConversationId
	/** How the place is named — "#allgemein", or the people of a direct conversation. */
	label: string
	/**
	 * The row's second half, where there is one worth drawing. It is absent when it would only repeat
	 * the first: "#budget budget" is one thing said twice (X-B1 P4).
	 */
	detail?: string
	href: string
}

/**
 * Where this person may write, in the order the sidebar shows them, with the conversations that
 * order leaves out (a hidden one) behind them. It is the quick switcher's list minus the two kinds
 * of row that have no composer — a public channel nobody joined, and a person with no conversation —
 * and a shared selector for "the conversations I can post in" would be the right home for it once a
 * third surface needs one.
 *
 * How a row reads is the switcher's rule and not a second one: `describeRow` decides which half goes
 * first and whether there is a second half at all, from the same model, so a channel is named here
 * exactly as the palette names it — "Engineering · #eng", and "#design" alone where the name is only
 * the slug capitalised.
 */
function destinations(model: Model): Destination[] {
	const slice = chatSlice(model)
	const meId = model.me?.id ?? 0
	const inSidebar = sidebarOrder(model)
	const shown = new Set(inSidebar)
	const rows: Destination[] = []
	// What `describeRow` recognises a row by: the address of every conversation it is handed.
	const byAddress = new Map<string, ConversationView>()
	const targets: { conv: ConversationId; href: string; target: SwitchTarget }[] = []
	for (const conv of [...inSidebar, ...model.conversationOrder.filter((id) => !shown.has(id))]) {
		if (slice.self[conv]?.state !== "active") continue
		const meta = model.conversations[conv]?.meta
		if (meta === undefined) continue
		const href = conversationHref(meta)
		if (href === null) continue
		byAddress.set(href, meta)
		targets.push({
			conv,
			href,
			target: {
				id: String(conv),
				label: conversationAddress(meta, model.users, meId),
				detail: isDirect(meta.kind)
					? `@${meta.dmHandle ?? ""}`
					: conversationTitle(meta, model.users, meId),
				to: href,
			},
		})
	}
	for (const { conv, href, target } of targets) {
		const row = describeRow(target, byAddress)
		rows.push({
			conv,
			label: row.label,
			...(row.detail === undefined ? {} : { detail: row.detail }),
			href,
		})
	}
	return rows
}

export function ForwardDialog({
	msg,
	authorName,
	onClose,
}: {
	msg: MessageView
	/** Who wrote the message, as the row already resolved it. */
	authorName: string
	onClose: () => void
}) {
	const [query, setQuery] = useState("")
	const [target, setTarget] = useState<ConversationId>(0)
	const [note, setNote] = useState("")
	/** A refusal that is this device's rather than the server's: the local draft cap. */
	const [refused, setRefused] = useState<string | null>(null)
	const store = useDraftStore()

	// Read once rather than subscribed to: the list of places is a snapshot of the moment somebody
	// chose to forward, and a conversation arriving while the dialog is open must not move the row
	// under the pointer. The dialog is mounted only while it is open (`MessageRow`), so the read
	// happens when it opens.
	const [places] = useState(() => destinations(client.api.getState()))

	const rows = useMemo(() => {
		const scored: { place: Destination; score: number }[] = []
		for (const place of places) {
			const score = scoreTarget(query, `${place.label} ${place.detail ?? ""}`)
			if (score !== null) scored.push({ place, score })
		}
		scored.sort((a, b) => b.score - a.score)
		return scored.slice(0, MAX_ROWS).map((row) => row.place)
	}, [places, query])

	const chosen =
		rows.find((row) => row.conv === target) ?? places.find((row) => row.conv === target)

	const forward = useAsyncAction(async () => {
		if (chosen === undefined) return
		const model = client.api.getState()
		const from = model.conversations[msg.conv]?.meta
		const path = from === undefined ? null : messageHref(from, msg.seq)
		// The permalink goes in as an explicit markdown link rather than as the bare URL: a bare URL
		// becomes a link only where the host carries a dot (goldmark's linkify rule — not on a
		// development stack), and the formatted editor drops the angle brackets of the `<url>` form on
		// its way back to markdown. `[label](url)` survives both, and its label is the one the
		// permalink chip draws anyway.
		const link =
			path === null
				? ""
				: `[${t("chat.render.permalink", { seq: msg.seq })}](${globalThis.location.origin}${path})`
		const head = t("chat.forward.quote", {
			author: authorName,
			place: from === undefined ? "" : conversationAddress(from, model.users, model.me?.id ?? 0),
			date: formatDate(msg.createdAt),
			link,
		})
		const written = await store.append(
			keyOf({ kind: "conversation", conv: chosen.conv }),
			buildForwardQuote({ head, markdown: msg.markdown, note: note.trim() }),
		)
		// A draft over the local cap is refused whole rather than shortened (`draftStore.ts`), so a
		// forward into a box that is already full writes nothing. Saying so and staying put is the only
		// honest answer: navigating to a conversation whose box did not change is a silent loss.
		if (written === "tooLarge") {
			setRefused("chat.forward.tooLarge")
			return
		}
		onClose()
		goTo(chosen.href)
		focusComposerWhenItArrives()
	})

	// The three slots of the dialog shell do the work here: the list of places and the note are the
	// **body**, which is the only thing that scrolls, and the two buttons are the **actions** row
	// above its rule, which a list of twenty destinations can no longer push off the screen. The
	// older `footer` alias with a wrapper of its own inside it is what that used to be.
	return (
		<Dialog
			open
			onOpenChange={(next) => {
				if (!next) onClose()
			}}
			title={t("chat.forward.title")}
			description={t("chat.forward.body")}
			actions={
				<>
					<Button variant="neutral" label={coreT("action.cancel")} onClick={onClose} />
					<Button
						variant="primary"
						label={t("chat.forward.send")}
						disabled={chosen === undefined || forward.busy}
						onClick={() => void forward.run()}
					/>
				</>
			}
		>
			{/* The picker is a group of radio buttons, not a listbox: that is the control a browser and
			    a screen reader already agree about — one tab stop, the arrow keys inside it — and every
			    row is a real label (the same reason `PeoplePicker` is a group of check boxes). */}
			<fieldset className="tb-form tb-forward__group">
				<legend className="tb-field__label">{t("chat.forward.places")}</legend>
				<Field label={t("chat.forward.filter")}>
					{(props) => (
						<input
							{...props}
							className="tb-field__control"
							type="search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
					)}
				</Field>
				{rows.length === 0 ? (
					<p className="tb-empty">{t("chat.forward.noPlaces")}</p>
				) : (
					<ul className="tb-forward__list">
						{rows.map((place) => (
							<li key={place.conv}>
								<label className="tb-forward__option">
									<input
										type="radio"
										name="tb-forward-target"
										checked={place.conv === target}
										onChange={() => {
											setTarget(place.conv)
											// The refusal names the box of the conversation it was about, so it
											// cannot outlive the choice that produced it.
											setRefused(null)
										}}
									/>
									<span className="tb-forward__place">{place.label}</span>
									{place.detail === undefined ? null : (
										<span className="tb-hint">{place.detail}</span>
									)}
								</label>
							</li>
						))}
					</ul>
				)}
			</fieldset>
			<div className="tb-fields">
				<Field label={t("chat.forward.note")} hint={t("chat.forward.noteHint")}>
					{(props) => (
						<textarea
							{...props}
							className="tb-field__control"
							rows={2}
							value={note}
							onChange={(event) => setNote(event.target.value)}
						/>
					)}
				</Field>
			</div>
			<FormAlert messageKey={refused ?? forward.error} />
		</Dialog>
	)
}
